import type { ReactNode } from 'react';

import type { ApiAddressWithFormData } from 'api/types/ApiAddress';
import { InputField } from 'components/Form/InputField';
import { LocationField } from 'components/Form/LocationField';
import { RadioButtonsField } from 'components/Form/Radio/RadioButtonsField';
import { InfoBox } from 'components/InfoBox/InfoBox';
import { BodySmall } from 'components/Text/BodySmall';
import { H5 } from 'components/Text/H5';
import { useI18n } from 'i18n/hooks/useI18n';
import type { SearchLocation } from 'modules/search/types/SearchLocation';
import { RichError } from 'utils/RichError';
import type { SerializedAddressComponent } from 'utils/googleAddresses';
import { serializeAddress } from 'utils/googleAddresses';
import { trackError } from 'utils/trackError';

import { AddressMap } from './AddressMap';
import { AllFieldToggle } from './AllFieldToggle';
import { UseAddressButton } from './UseAddressButton';

type Props = {
  address: ApiAddressWithFormData | null | undefined;
  clearErrors: () => void;
  showDescription?: boolean;
  editedValue: string | null | undefined;
  canChangeLocationHidden?: boolean;
  selectAutoCompleteSuggestion: (
    addressUpdate: SerializedAddressComponent,
  ) => void;
  overrideLatLong: (address: { latitude: number; longitude: number }) => void;
  onManualModifiedValue: (location: SearchLocation | undefined) => void;
  onLocationHiddenChange: () => void;
  error: string | null | undefined;
  disabled: boolean;
  showLocationButtonText?: string;
  hideLocationButtonText?: string;
  useOrgAddress?: () => void;
  cityOnly: boolean;
  toggleSeparateFields: () => void;
  namePrefix: string;
  disableOrgAddressButton?: boolean;
  label?: ReactNode;
};

export function AddressFields({
  address,
  clearErrors,
  showDescription,
  editedValue,
  canChangeLocationHidden,
  selectAutoCompleteSuggestion,
  overrideLatLong,
  onManualModifiedValue,
  onLocationHiddenChange,
  error,
  disabled,
  showLocationButtonText,
  hideLocationButtonText,
  useOrgAddress,
  cityOnly,
  toggleSeparateFields,
  namePrefix,
  disableOrgAddressButton = false,
  label,
}: Props) {
  const { i18n } = useI18n();

  const namePrefixDash = namePrefix.replace(/\./g, '-');

  const handlePlaceDetails = (place: google.maps.GeocoderResult) => {
    const serializedAddress = serializeAddress(i18n, place);
    selectAutoCompleteSuggestion(serializedAddress);

    // https://developers.google.com/maps/documentation/javascript/reference/places-widget#Autocomplete.getPlace  -- failure case
    if (!serializedAddress.city) {
      trackError(
        // @ts-expect-error TS(2345): Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
        new RichError('Failed to fetch API result', JSON.stringify(place)),
      );
    }
  };

  return (
    <div data-qa-id={`${namePrefixDash}-section`}>
      {label && (
        <div style={{ marginBottom: 16 }}>
          <H5>{label}</H5>
        </div>
      )}
      {error && (
        <InfoBox className="error" $type="danger" $marginBottom={4}>
          {error}
        </InfoBox>
      )}
      {canChangeLocationHidden && (
        <RadioButtonsField
          name={`${namePrefix}.locationHidden`}
          buttons={[
            {
              label: showLocationButtonText ?? i18n('Show full street address'),
              value: false,
            },
            {
              label: hideLocationButtonText ?? i18n('Keep address private'),
              value: true,
            },
          ]}
          disabled={disabled}
          onChange={onLocationHiddenChange}
        />
      )}
      {!disableOrgAddressButton && useOrgAddress && (
        <div style={{ marginTop: canChangeLocationHidden ? -12 : undefined }}>
          <UseAddressButton onClick={useOrgAddress} disabled={disabled} />
        </div>
      )}
      {showDescription && (
        <InputField
          name={`${namePrefix}.description`}
          label={
            /* TRANSLATORS: Form field for address description */
            i18n('Location name')
          }
          placeholder={
            /* TRANSLATORS: Form field for address description placeholder */
            i18n('e.g. My House, Soup Kitchen, Hospital (optional)')
          }
          disabled={disabled}
        />
      )}

      <div style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', zIndex: 1, right: 0 }}>
          <AllFieldToggle
            clearErrors={clearErrors}
            qaId={`${namePrefixDash}-show-all-fields`}
            toggleSeparateFields={toggleSeparateFields}
            text={i18n('Show all address fields')}
          />
        </div>

        {cityOnly ? (
          <LocationField
            qaId={`${namePrefixDash}-city-full`}
            handlePlaceDetails={handlePlaceDetails}
            name={`${namePrefix}.full`}
            disabled={disabled}
            addressTypes={['(cities)']}
            label={
              /* TRANSLATORS: Form field for address city / state */
              `${i18n('City')}${'*'}`
            }
            noWrap
            placeholder={i18n('Lake Wobegon, MN')}
            onChange={onManualModifiedValue}
            customErrorMatch={(errors) => errors.employerStateCode}
            locationText={editedValue}
            includeFull
          />
        ) : (
          <>
            <LocationField
              handlePlaceDetails={handlePlaceDetails}
              name={`${namePrefix}.full`}
              disabled={disabled}
              addressTypes={['address']}
              label={
                /* TRANSLATORS: Form field for address city / state */
                `${i18n('Address')}${'*'}`
              }
              noWrap
              placeholder={i18n('Please select a suggestion')}
              onChange={onManualModifiedValue}
              customErrorMatch={(errors) => errors[`${namePrefix}.full`]}
              locationText={editedValue}
              includeCity
              includeFull
            />
            <InputField
              name={`${namePrefix}.line2`}
              disabled={disabled}
              label={
                /* TRANSLATORS: Form field for address line 2 */
                i18n('Apt / Floor / Unit')
              }
              placeholder={
                /* TRANSLATORS: Form field for address line 2 */
                i18n('e.g., Suite 201 (optional)')
              }
            />
          </>
        )}

        {address?.latitude && address?.longitude && address?.full && (
          <>
            <BodySmall
              qaId={`${namePrefixDash}-latlong-address`}
              mb={2}
              color="lightContentGrey"
            >{`${i18n('Preview of Location')}: ${address.full}`}</BodySmall>
            <AddressMap
              latitude={address.latitude}
              longitude={address.longitude}
              updateAddress={overrideLatLong}
              disabled={disabled}
              qaId={`${namePrefixDash}-map`}
            />
          </>
        )}
      </div>

      {/* These are just for the tests. */}
      {/* TODO: don't include in production */}
      <input
        type="hidden"
        value={address?.latitude ?? ''}
        data-qa-id={`${namePrefixDash}-latitude`}
      />
      <input
        type="hidden"
        value={address?.longitude ?? ''}
        data-qa-id={`${namePrefixDash}-longitude`}
      />
    </div>
  );
}
